// ./src/utils/api/PartnerApplicationService.ts
//
// PartnerApplication — an inbound sponsorship application (#A4.5). Applications arrive from the
// PUBLIC event website; a host never creates one, they only review it. So this service exposes
// list / get / approve / reject and nothing else.
//
// ⚠ APPROVE AND REJECT ARE NOT STATUS UPDATES. Approving takes a place on the offering under a row
// lock and creates the confirmed partner; rejecting REFUNDS the sponsor when they already paid.
// Both are server actions that take an id and an optional note — a client that could set the status
// directly could approve without taking a place, or refuse without returning the money.
// See em-scaffold-ui.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { Pagination, SortItem } from "./commonApiTypes";

// =============================================================================
// Vocabulary — mirrors src/boundary/partner.ts
// =============================================================================

export const PARTNER_APPLICATION_STATUS = {
  PENDING: "pending",
  APPROVED: "approved",
  REJECTED: "rejected",
  WITHDRAWN: "withdrawn",
} as const;

export const PARTNER_APPLICATION_STATUS_LABELS: Record<string, string> = {
  [PARTNER_APPLICATION_STATUS.PENDING]: "Waiting on you",
  [PARTNER_APPLICATION_STATUS.APPROVED]: "Approved",
  [PARTNER_APPLICATION_STATUS.REJECTED]: "Declined",
  [PARTNER_APPLICATION_STATUS.WITHDRAWN]: "Withdrawn",
};

export const PARTNER_APPLICATION_STATUS_COLORS: Record<string, string> = {
  [PARTNER_APPLICATION_STATUS.PENDING]: "orange",
  [PARTNER_APPLICATION_STATUS.APPROVED]: "green",
  [PARTNER_APPLICATION_STATUS.REJECTED]: "gray",
  [PARTNER_APPLICATION_STATUS.WITHDRAWN]: "gray",
};

export const partnerApplicationStatusLabel = (status?: string | null): string =>
  (status && PARTNER_APPLICATION_STATUS_LABELS[status]) || "—";

/** Money already taken — the only state in which declining sends a refund. */
export const PARTNER_APPLICATION_PAYMENT_STATUS = {
  UNPAID: "unpaid",
  PARTIAL: "partial",
  PAID: "paid",
  WAIVED: "waived",
} as const;

// =============================================================================
// GraphQL
// =============================================================================

export const PARTNER_APPLICATION_FIELDS = `
  id
  accountId
  eventId
  eventPartnerPackageId
  partnerKind
  orgName
  firstName
  lastName
  email
  phone
  websiteUrl
  message
  applicationStatus
  reviewNotes
  reviewedAt
  reviewedByUserId
  eventPartnerId
  personId
  visitorId
  amount
  serviceFeeAmount
  totalAmount
  feeMode
  applicationFeeAmount
  currencyCode
  paymentStatus
  submittedAt
  status
  createdAt
  updatedAt
`;

const LIST_QUERY = gql`
  query PartnerApplicationList($filter: PartnerApplicationFilter, $params: PaginationAndSorting) {
    partnerApplicationList(filter: $filter, params: $params) {
      code
      data { ${PARTNER_APPLICATION_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const GET_QUERY = gql`
  query PartnerApplicationGet($id: ID) {
    partnerApplicationGet(id: $id) {
      code
      data { ${PARTNER_APPLICATION_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const APPROVE_MUTATION = gql`
  mutation PartnerApplicationApprove($params: PartnerApplicationReviewInput) {
    partnerApplicationApprove(params: $params) {
      code
      data { ${PARTNER_APPLICATION_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const WITHDRAW_MUTATION = gql`
  mutation PartnerApplicationWithdraw($params: PartnerApplicationReviewInput) {
    partnerApplicationWithdraw(params: $params) {
      code
      data { ${PARTNER_APPLICATION_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const REJECT_MUTATION = gql`
  mutation PartnerApplicationReject($params: PartnerApplicationReviewInput) {
    partnerApplicationReject(params: $params) {
      code
      data { ${PARTNER_APPLICATION_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types
// =============================================================================

export interface PartnerApplication {
  id?: string;
  accountId?: string | null;
  eventId?: string | null;
  eventPartnerPackageId?: string | null;
  partnerKind?: string | null;
  orgName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  websiteUrl?: string | null;
  message?: string | null;
  applicationStatus?: string | null;
  reviewNotes?: string | null;
  reviewedAt?: string | null;
  reviewedByUserId?: string | null;
  /** The confirmed partner created on approval. */
  eventPartnerId?: string | null;
  personId?: string | null;
  visitorId?: string | null;
  /** ⚠ What the HOST is owed — the price quoted WHEN THEY APPLIED, not today's. */
  amount?: number | null;
  /** Our platform service fee, frozen at the same moment. Read-only. */
  serviceFeeAmount?: number | null;
  /** ⚠ What the SPONSOR's card was actually charged. Larger than `amount` when fees are passed on. */
  totalAmount?: number | null;
  /** The event's fee mode when they applied: absorb | service | service_processor. */
  feeMode?: string | null;
  /**
   * What THIS applicant agreed was non-refundable, frozen when they applied.
   * ⚠ A decline or withdrawal returns `totalAmount` minus this. Null/0 = everything comes back.
   */
  applicationFeeAmount?: number | null;
  currencyCode?: string | null;
  paymentStatus?: string | null;
  submittedAt?: string | null;
  status?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface PartnerApplicationFilter {
  id?: string[] | null;
  eventId?: string[] | null;
  eventPartnerPackageId?: string[] | null;
  applicationStatus?: string[] | null;
  searchKeyword?: string | null;
}

// =============================================================================
// Helpers
// =============================================================================

/** Only a PAID application refunds on decline — the one thing the confirm dialog must say. */
export const applicationWillRefund = (a: PartnerApplication): boolean =>
  a.paymentStatus === PARTNER_APPLICATION_PAYMENT_STATUS.PAID;

/**
 * The applicant's own name, for display. Empty when they gave neither part — an organization can
 * apply from info@ without naming a human, which is not an error.
 */
export const applicantName = (a: PartnerApplication): string =>
  [a.firstName, a.lastName].filter(Boolean).join(" ").trim();

/** A decision has been made — approve/reject are no longer offered. */
export const applicationIsDecided = (a: PartnerApplication): boolean =>
  a.applicationStatus === PARTNER_APPLICATION_STATUS.APPROVED ||
  a.applicationStatus === PARTNER_APPLICATION_STATUS.REJECTED;

// =============================================================================
// Service
// =============================================================================

export class PartnerApplicationService {
  static async list(
    filter?: PartnerApplicationFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    return ApiService.executeQuery(LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(GET_QUERY, { id });
  }

  /** Takes a place on the offering and creates the confirmed partner. Idempotent server-side. */
  static async approve(id: string, reviewNotes?: string | null): Promise<OpResult> {
    return ApiService.executeMutation(APPROVE_MUTATION, { params: { id, reviewNotes: reviewNotes || null } });
  }

  /** ⚠ Refunds the sponsor when they have already paid. A failed refund fails the whole rejection. */
  static async reject(id: string, reviewNotes?: string | null): Promise<OpResult> {
    return ApiService.executeMutation(REJECT_MUTATION, { params: { id, reviewNotes: reviewNotes || null } });
  }

  /**
   * The sponsor pulled out.
   *
   * ⚠ `refundApproved` only applies to an APPROVED application, and it is stated explicitly every
   * time — there is no default, because the host is deciding whether money goes back. A PENDING
   * withdrawal always refunds (less the non-refundable fee) and ignores this flag: nothing had been
   * decided, so keeping the payment would be a charge for nothing.
   */
  static async withdraw(id: string, reviewNotes?: string | null, refundApproved?: boolean): Promise<OpResult> {
    return ApiService.executeMutation(WITHDRAW_MUTATION, {
      params: { id, reviewNotes: reviewNotes || null, refundApproved: refundApproved === true },
    });
  }
}
